import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";
import { Dialog } from "./Dialog";
import {
  ContractorFormDialog,
  type ContractorFormValues,
} from "./ContractorFormDialog";

interface Contractor {
  _id: string;
  name: string;
  serviceType: string;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

interface HistoryItem {
  _id: string;
  title: string;
  status: "requested" | "scheduled" | "completed" | "cancelled";
  scheduledFor: string | null;
  completedAt: number | null;
  property: { _id: string; name: string } | null;
  unit: { _id: string; label: string } | null;
}

// Detail view for a contractor — contact info on top, full work
// history below (across all properties). Edit / delete actions in the
// footer for landlords.
export function ContractorDetailDialog({
  open,
  onClose,
  contractor,
}: {
  open: boolean;
  onClose: () => void;
  contractor: Contractor | null;
}) {
  const history = useQuery(
    anyApi.maintenance.listByContractor,
    open && contractor ? { contractorId: contractor._id } : "skip",
  ) as HistoryItem[] | null | undefined;

  const remove = useMutation(anyApi.contractors.remove);
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!contractor) return null;

  const handleDelete = async () => {
    if (!confirm(`Delete ${contractor.name}? This can't be undone.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await remove({ contractorId: contractor._id });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const formValues: ContractorFormValues = {
    _id: contractor._id,
    name: contractor.name,
    serviceType: contractor.serviceType,
    email: contractor.email,
    phone: contractor.phone,
    notes: contractor.notes,
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} title={contractor.name} width="lg">
        <div className="px-6 py-5 space-y-6">
          <ContactInfo contractor={contractor} />
          <History history={history} />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-100 -mx-6 px-6 pt-4">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="mr-auto text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 transition-colors"
            >
              Delete contractor
            </button>
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
            >
              Edit
            </button>
          </div>
        </div>
      </Dialog>

      <ContractorFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        existing={formValues}
      />
    </>
  );
}

function ContactInfo({ contractor }: { contractor: Contractor }) {
  return (
    <section>
      <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
        Contact
      </h3>
      <dl className="mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-slate-500">Service</dt>
        <dd className="text-slate-900">{contractor.serviceType}</dd>
        {contractor.email && (
          <>
            <dt className="text-slate-500">Email</dt>
            <dd className="text-slate-900">
              <a
                href={`mailto:${contractor.email}`}
                className="hover:underline"
              >
                {contractor.email}
              </a>
            </dd>
          </>
        )}
        {contractor.phone && (
          <>
            <dt className="text-slate-500">Phone</dt>
            <dd className="text-slate-900">
              <a
                href={`tel:${contractor.phone}`}
                className="hover:underline"
              >
                {contractor.phone}
              </a>
            </dd>
          </>
        )}
        {contractor.notes && (
          <>
            <dt className="text-slate-500">Notes</dt>
            <dd className="text-slate-700 whitespace-pre-wrap">
              {contractor.notes}
            </dd>
          </>
        )}
      </dl>
    </section>
  );
}

function History({
  history,
}: {
  history: HistoryItem[] | null | undefined;
}) {
  return (
    <section>
      <h3 className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-400">
        Work history
      </h3>
      <Body history={history} />
    </section>
  );
}

function Body({ history }: { history: HistoryItem[] | null | undefined }) {
  if (history === undefined) {
    return (
      <p className="mt-3 text-sm text-slate-500">Loading…</p>
    );
  }
  if (history === null) {
    return (
      <p className="mt-3 text-sm text-slate-500">Not authorized to view.</p>
    );
  }
  if (history.length === 0) {
    return (
      <p className="mt-3 text-sm text-slate-500">
        No tasks yet — they'll show up here once you assign one to this
        contractor in the maintenance schedule.
      </p>
    );
  }
  return (
    <ul className="mt-3 divide-y divide-slate-100">
      {history.map((task) => (
        <li
          key={task._id}
          className="py-3 flex items-start gap-4 first:pt-0"
        >
          <DateCell task={task} />
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3 flex-wrap">
              <p className="text-sm font-medium text-slate-900">
                {task.title}
              </p>
              <StatusBadge status={task.status} />
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              {[
                task.property?.name ?? "Unknown property",
                task.unit ? task.unit.label : "Property-wide",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function DateCell({ task }: { task: HistoryItem }) {
  const ts =
    task.completedAt ??
    (task.scheduledFor ? new Date(task.scheduledFor).getTime() : null);
  if (ts === null) {
    return (
      <div className="w-16 shrink-0 text-right">
        <p className="text-sm font-medium text-slate-400">TBD</p>
      </div>
    );
  }
  const d = new Date(ts);
  const month = d.toLocaleString("en-US", { month: "short" });
  const day = d.getDate();
  const year = d.getFullYear();
  const now = new Date().getFullYear();
  return (
    <div className="w-16 shrink-0 text-right">
      <p className="text-sm font-semibold text-slate-900">
        {month} {day}
      </p>
      {year !== now && <p className="text-[10px] text-slate-500">{year}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: HistoryItem["status"] }) {
  const styles: Record<HistoryItem["status"], string> = {
    scheduled: "bg-blue-50 text-blue-700 ring-blue-200",
    requested: "bg-amber-50 text-amber-700 ring-amber-200",
    completed: "bg-slate-100 text-slate-600 ring-slate-200",
    cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles[status]}`}
    >
      {status}
    </span>
  );
}
